import type { Currency } from './currency.js';
import type { LookupItemDto, TeamRequirement } from './inquiry.js';
import { InquiryVersioningChoice } from './inquiry.js';
import type { PricingModel } from './offer.js';
import { AccountType, type AccountType as AccountTypeT } from './account.js';

// --- Enums (const object maps) ---

export const AgentType = {
  InquiryAdvisor: 'InquiryAdvisor',
  OfferAdvisor: 'OfferAdvisor',
  InquirerGuide: 'InquirerGuide',
  Generic: 'Generic',
  PerformanceDiagnostics: 'PerformanceDiagnostics',
  CostReduction: 'CostReduction',
  ProcessImprovement: 'ProcessImprovement',
  SupplyChainOptimization: 'SupplyChainOptimization',
  DigitalAutomation: 'DigitalAutomation',
  StrategyTransformation: 'StrategyTransformation',
  DecisionAgent: 'DecisionAgent',
  ProjectAgent: 'ProjectAgent',
} as const;
export type AgentType = (typeof AgentType)[keyof typeof AgentType];

export const AgentAudience = {
  Inquirer: 'inquirer',
  Supplier: 'supplier',
  Both: 'both',
} as const;
export type AgentAudience = (typeof AgentAudience)[keyof typeof AgentAudience];

/** Which account category an agent config is the default for. */
export const AgentDefaultScope = {
  Inquirer: 'inquirer',
  Supplier: 'supplier',
  Other: 'other',
} as const;
export type AgentDefaultScope = (typeof AgentDefaultScope)[keyof typeof AgentDefaultScope];

export const AgentDefaultScopeValues = Object.values(AgentDefaultScope);

export function agentDefaultScopeForAccountType(
  accountType: AccountTypeT | null | undefined,
): AgentDefaultScope {
  if (accountType === AccountType.Inquirer) {
    return AgentDefaultScope.Inquirer;
  }
  if (
    accountType === AccountType.Consultant ||
    accountType === AccountType.SolutionProvider
  ) {
    return AgentDefaultScope.Supplier;
  }
  return AgentDefaultScope.Other;
}

export const LlmChatSessionStatus = {
  Active: 'Active',
  Completed: 'Completed',
} as const;
export type LlmChatSessionStatus = (typeof LlmChatSessionStatus)[keyof typeof LlmChatSessionStatus];

export const LlmChatMessageRole = {
  User: 'User',
  Assistant: 'Assistant',
  System: 'System',
  Tool: 'Tool',
} as const;
export type LlmChatMessageRole = (typeof LlmChatMessageRole)[keyof typeof LlmChatMessageRole];

export const LlmProviderKind = {
  Gemini: 'gemini',
  Anthropic: 'anthropic',
  Openai: 'openai',
  Bedrock: 'bedrock',
} as const;
export type LlmProviderKind = (typeof LlmProviderKind)[keyof typeof LlmProviderKind];

export const SystemPromptSource = {
  Manual: 'manual',
  Langfuse: 'langfuse',
} as const;
export type SystemPromptSource = (typeof SystemPromptSource)[keyof typeof SystemPromptSource];

export const LangfusePromptType = {
  Text: 'text',
  Chat: 'chat',
} as const;
export type LangfusePromptType = (typeof LangfusePromptType)[keyof typeof LangfusePromptType];

export interface LangfusePromptMetaDto {
  name: string;
  type: LangfusePromptType;
  labels: string[];
  tags: string[];
  versions: number[];
  lastUpdatedAt: string;
}

/**
 * Allowed {{variable}} keys in Langfuse prompts (Mustache-style).
 * inquiry_* keys are empty when the chat session has no linked inquiry.
 */
export const LANGFUSE_PROMPT_VARIABLE_KEYS = [
  'agent_display_name',
  'inquiry_id',
  'inquiry_title',
  'inquiry_objectives',
  'inquiry_specification',
  'inquiry_status',
  'locale',
] as const;
export type LangfusePromptVariableKey =
  (typeof LANGFUSE_PROMPT_VARIABLE_KEYS)[number];

export type LangfusePromptVariables = Record<LangfusePromptVariableKey, string>;

/** Sample values for admin preview and documentation. */
export const PROMPT_VARIABLE_FIXTURES: LangfusePromptVariables = {
  agent_display_name: '[Náhled] Ukázkový agent',
  inquiry_id: 'preview-inquiry-id',
  inquiry_title: '[Náhled] Ukázkový projekt',
  inquiry_objectives: 'Ukázkové cíle projektu pro náhled promptu.',
  inquiry_specification: 'Ukázková specifikace pro náhled promptu.',
  inquiry_status: 'draft',
  locale: 'cs',
};

const LANGFUSE_VARIABLE_PATTERN = /\{\{\s*(\w+)\s*\}\}/g;

/** Extract unique {{variable}} names from a Langfuse prompt template. */
export function extractLangfuseVariableNames(template: string): string[] {
  const names = new Set<string>();
  for (const match of template.matchAll(LANGFUSE_VARIABLE_PATTERN)) {
    if (match[1]) names.add(match[1]);
  }
  return [...names];
}

/** Substitute {{key}} placeholders when Langfuse client is unavailable. */
export function substituteLangfuseTemplateVariables(
  template: string,
  vars: Record<string, string>,
): string {
  return template.replace(LANGFUSE_VARIABLE_PATTERN, (_, key: string) => vars[key] ?? '');
}

export interface LangfusePromptDetailDto {
  name: string;
  type: LangfusePromptType;
  version: number;
  labels: string[];
  resolvedSystemPrompt: string;
  hasVariables: boolean;
  variableNames: string[];
}

// --- Knowledge Sources ---

export interface KnowledgeSourcesConfig {
  /** Enable the provider's native web search tool (Google Search for Gemini, web search for OpenAI/Anthropic). */
  webSearch?: boolean;
  /** Inject a compact "Account profile" section (latest account observations) into the system prompt. */
  accountObservations?: boolean;
}

export const AgentKnowledgeConversionStatus = {
  Pending: 'pending',
  Completed: 'completed',
  Failed: 'failed',
} as const;
export type AgentKnowledgeConversionStatus =
  (typeof AgentKnowledgeConversionStatus)[keyof typeof AgentKnowledgeConversionStatus];

/** Persisted knowledge file with audit original + converted markdown metadata */
export interface AgentKnowledgeFileDto {
  id: string;
  agentConfigId: string;
  originalName: string;
  originalMimeType: string;
  conversionStatus: AgentKnowledgeConversionStatus;
  createdAt: string;
}

export interface LlmModelDto {
  id: string;
  displayName: string | null;
  provider: LlmProviderKind;
}

export interface AdminLlmModelDto {
  id: string;
  provider: LlmProviderKind;
  modelId: string;
  displayName: string | null;
  isEnabled: boolean;
  isAvailable: boolean;
  /** Price per 1M input tokens in micro-USD; null = unknown (usage cost not estimated). */
  inputPricePerMTokMicroUsd: number | null;
  cachedInputPricePerMTokMicroUsd: number | null;
  outputPricePerMTokMicroUsd: number | null;
  lastSyncedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateLlmModelDto {
  isEnabled: boolean;
  inputPricePerMTokMicroUsd?: number | null;
  cachedInputPricePerMTokMicroUsd?: number | null;
  outputPricePerMTokMicroUsd?: number | null;
}

export interface LlmModelSyncDto {
  /** When true, replace existing Bedrock prices with AWS Price List values where available. */
  overwriteBedrockPrices?: boolean;
}

export interface LlmModelSyncResultDto {
  added: number;
  updated: number;
  unavailable: number;
  /** Bedrock price fields set from AWS Price List during sync (empty fill or overwrite). */
  pricesFilled: number;
}

// --- DTOs ---

export interface AgentConfigDto {
  id: string;
  agentType: AgentType;
  displayName: string;
  description: string;
  audience: AgentAudience;
  isCustom: boolean;
  requiresInquiry: boolean;
  isDefaultInquirer: boolean;
  isDefaultSupplier: boolean;
  isDefaultOther: boolean;
}

export function isAgentDefaultForScope(
  config: Pick<
    AgentConfigDto,
    'isDefaultInquirer' | 'isDefaultSupplier' | 'isDefaultOther'
  >,
  scope: AgentDefaultScope,
): boolean {
  switch (scope) {
    case AgentDefaultScope.Inquirer:
      return config.isDefaultInquirer;
    case AgentDefaultScope.Supplier:
      return config.isDefaultSupplier;
    case AgentDefaultScope.Other:
      return config.isDefaultOther;
  }
}

export interface LlmChatSessionDto {
  id: string;
  accountId: string | null;
  createdById: string;
  agentType: AgentType;
  provider: LlmProviderKind;
  model: string | null;
  title: string | null;
  status: LlmChatSessionStatus;
  isPreview?: boolean;
  pinned?: boolean;
  inquiryId?: string | null;
  projectConceptId?: string | null;
  resultData?: LlmChatInquiryDraftDto | LlmChatOfferDraftDto | null;
  createdAt: string;
  updatedAt: string;
}

/** Admin list row for LLM chat sessions. */
export interface AdminLlmChatSessionListItemDto {
  id: string;
  title: string | null;
  agentType: AgentType;
  model: string | null;
  status: LlmChatSessionStatus;
  createdAt: string;
  updatedAt: string;
  accountId: string | null;
  accountName: string | null;
  createdById: string;
  createdByEmail: string;
  messageCount: number;
}

export interface AdminLlmChatSessionListDto {
  items: AdminLlmChatSessionListItemDto[];
  total: number;
  page: number;
  limit: number;
}

export interface AgentConfigPreviewSessionDto {
  session: LlmChatSessionDto;
  messages: LlmChatMessageDto[];
}

export interface LlmChatAttachmentDto {
  id: string;
  messageId: string | null;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
}

/** Summary of best-effort LLM chat session linking and attachment copy on resource create. */
export interface ChatSessionIntegrationResult {
  sessionId: string;
  linked: boolean;
  attachmentsCopied: number;
  attachmentsSkipped: number;
  warnings: string[];
}

export interface ToolCallDto {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
  thoughtSignature?: string;
}

export interface ToolResultDto {
  toolCallId: string;
  result: unknown;
}

export const LlmChatUiActionType = {
  InquiryVersioningChoice: 'inquiry_versioning_choice',
  AgentQuestion: 'agent_question',
} as const;
export type LlmChatUiActionType = (typeof LlmChatUiActionType)[keyof typeof LlmChatUiActionType];

export interface LlmChatUiActionInquiryVersioningOptionDto {
  value: InquiryVersioningChoice;
}

export interface LlmChatUiActionAgentQuestionOptionDto {
  value: string;
  label: string;
  description?: string;
}

export interface LlmChatUiActionInquiryVersioningChoice {
  type: typeof LlmChatUiActionType.InquiryVersioningChoice;
  inquiryId: string;
  options: LlmChatUiActionInquiryVersioningOptionDto[];
}

export interface LlmChatUiActionAgentQuestion {
  type: typeof LlmChatUiActionType.AgentQuestion;
  questionId: string;
  question: string;
  options: LlmChatUiActionAgentQuestionOptionDto[];
  allowCustomAnswer: boolean;
}

export type LlmChatMessageUiAction =
  | LlmChatUiActionInquiryVersioningChoice
  | LlmChatUiActionAgentQuestion;

export interface LlmChatMessageDto {
  id: string;
  sessionId: string;
  role: LlmChatMessageRole;
  content: string | null;
  toolCalls?: ToolCallDto[] | null;
  toolResults?: ToolResultDto[] | null;
  uiAction?: LlmChatMessageUiAction | null;
  attachments: LlmChatAttachmentDto[];
  /** Records referenced with `@`, resolved from the content tokens at read time - never stored. */
  references?: LlmChatEntityReferenceDto[];
  createdAt: string;
}

/** True when a user message exists after the prompt message (choice already submitted). */
export function isLlmChatUiActionResolved(
  message: LlmChatMessageDto,
  messages: LlmChatMessageDto[],
): boolean {
  if (!message.uiAction) return false;
  const promptIndex = messages.findIndex((m) => m.id === message.id);
  if (promptIndex === -1) return false;
  return messages.slice(promptIndex + 1).some((m) => m.role === LlmChatMessageRole.User);
}

export interface CreateLlmChatSessionDto {
  agentType: AgentType;
  inquiryId?: string;
  projectConceptId?: string;
  knowledgeContext?: Record<string, unknown>;
  provider?: LlmProviderKind;
  model?: string | null;
}

export interface UpdateLlmChatSessionModelDto {
  provider: LlmProviderKind;
  /** null = provider env default */
  model: string | null;
}

export interface UpdateLlmChatSessionPinDto {
  pinned: boolean;
}

export interface CreateLlmChatMessageDto {
  content: string;
  attachmentIds?: string[];
}

export interface AdminAgentConfigDto {
  id: string;
  agentType: AgentType;
  displayName: string;
  description: string | null;
  audience: AgentAudience;
  accountId: string | null;
  allowedUserIds: string[];
  provider: LlmProviderKind;
  model: string | null;
  systemPrompt: string;
  systemPromptSource: SystemPromptSource;
  langfusePromptName: string | null;
  langfusePromptLabel: string | null;
  availableTools: string[] | null;
  extractionSchema: Record<string, unknown> | null;
  knowledgeSources: KnowledgeSourcesConfig | null;
  isActive: boolean;
  isDraft: boolean;
  requiresInquiry: boolean;
  isDefaultInquirer: boolean;
  isDefaultSupplier: boolean;
  isDefaultOther: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAgentConfigDto {
  agentType: AgentType;
  displayName: string;
  description?: string | null;
  audience?: AgentAudience;
  accountId?: string | null;
  allowedUserIds?: string[];
  provider?: LlmProviderKind;
  model?: string | null;
  systemPrompt?: string;
  systemPromptSource?: SystemPromptSource;
  langfusePromptName?: string | null;
  langfusePromptLabel?: string | null;
  availableTools?: string[] | null;
  extractionSchema?: Record<string, unknown> | null;
  knowledgeSources?: KnowledgeSourcesConfig | null;
  isDraft?: boolean;
  requiresInquiry?: boolean;
  isDefaultInquirer?: boolean;
  isDefaultSupplier?: boolean;
  isDefaultOther?: boolean;
}

export interface UpdateAgentConfigDto {
  displayName?: string;
  description?: string | null;
  audience?: AgentAudience;
  accountId?: string | null;
  allowedUserIds?: string[];
  provider?: LlmProviderKind;
  model?: string | null;
  systemPrompt?: string;
  systemPromptSource?: SystemPromptSource;
  langfusePromptName?: string | null;
  langfusePromptLabel?: string | null;
  availableTools?: string[] | null;
  extractionSchema?: Record<string, unknown> | null;
  knowledgeSources?: KnowledgeSourcesConfig | null;
  isActive?: boolean;
  isDraft?: boolean;
  requiresInquiry?: boolean;
  isDefaultInquirer?: boolean;
  isDefaultSupplier?: boolean;
  isDefaultOther?: boolean;
}

export interface CreateUserAgentConfigDto {
  displayName: string;
  description?: string | null;
  systemPrompt: string;
  audience?: AgentAudience;
  allowedUserIds?: string[];
  provider?: LlmProviderKind;
  model?: string | null;
  knowledgeSources?: KnowledgeSourcesConfig | null;
  isDraft?: boolean;
  requiresInquiry?: boolean;
}

export interface UpdateUserAgentConfigDto {
  displayName?: string;
  description?: string | null;
  systemPrompt?: string;
  audience?: AgentAudience;
  allowedUserIds?: string[];
  provider?: LlmProviderKind;
  model?: string | null;
  knowledgeSources?: KnowledgeSourcesConfig | null;
  isDraft?: boolean;
  requiresInquiry?: boolean;
}

export interface UserAgentConfigDetailDto {
  id: string;
  agentType: AgentType;
  displayName: string;
  description: string;
  systemPrompt: string;
  audience: AgentAudience;
  allowedUserIds: string[];
  provider: LlmProviderKind;
  model: string | null;
  knowledgeSources: KnowledgeSourcesConfig | null;
  isCustom: boolean;
  isDraft: boolean;
  requiresInquiry: boolean;
}

export interface LlmChatInquiryDraftDto {
  title?: string;
  objectives?: string;
  specification?: string;
  categoryId?: LookupItemDto['id'];
  industryIds?: LookupItemDto['id'][];
  deliverableIds?: LookupItemDto['id'][];
  budgetMin?: number;
  budgetMax?: number;
  budgetCurrency?: string;
  currency?: Currency;
  startDate?: string;
  endDate?: string;
  deadline?: string;
  teamRequirement?: TeamRequirement;
  expertiseIds?: LookupItemDto['id'][];
  optionalReqs?: string;
}

export interface LlmChatOfferDraftLineItem {
  item?: string;
  quantity?: number;
  unit?: string;
  externalBusinessRate?: number;
}

export interface LlmChatOfferDraftMilestone {
  name?: string;
  startDate?: string;
  output?: string;
  acceptanceCriteria?: string;
  requiredCooperation?: string;
  dueDate?: string;
  price?: number;
}

export interface LlmChatOfferDraftTeamMember {
  role?: string;
  seniority?: string;
}

export interface LlmChatOfferDraftRisk {
  risk?: string;
  probability?: number;
  impact?: number;
  mitigation?: string;
}

export interface LlmChatOfferDraftDto {
  proposal?: string;
  outOfScope?: string;
  pricingModel?: PricingModel;
  currency?: Currency;
  lineItems?: LlmChatOfferDraftLineItem[];
  milestones?: LlmChatOfferDraftMilestone[];
  team?: LlmChatOfferDraftTeamMember[];
  risks?: LlmChatOfferDraftRisk[];
  sla?: string;
  validityDate?: string;
}

// --- One-shot inquiry field assist (e.g. objectives from keywords) ---

export const InquiryFieldAssistField = {
  Objectives: 'objectives',
  Specification: 'specification',
} as const;
export type InquiryFieldAssistField = (typeof InquiryFieldAssistField)[keyof typeof InquiryFieldAssistField];

/** Output caps aligned with UI / assistInquiryField slicing */
export const InquiryFieldAssistMaxLength = {
  Objectives: 300,
  Specification: 4000,
} as const;

export const InquiryFieldAssistMode = {
  Generate: 'generate',
  Refine: 'refine',
} as const;
export type InquiryFieldAssistMode = (typeof InquiryFieldAssistMode)[keyof typeof InquiryFieldAssistMode];

export interface InquiryFieldAssistContextDto {
  title?: string;
  currentText?: string;
  /** Step 1 objectives text - used when assisting specification for continuity */
  objectives?: string;
}

export interface InquiryFieldAssistRequestDto {
  field: InquiryFieldAssistField;
  mode: InquiryFieldAssistMode;
  /** Keywords, short notes, or extra instructions depending on mode */
  input: string;
  /** BCP 47 / app locale hint, e.g. cs, en - used for output language */
  locale?: string;
  context?: InquiryFieldAssistContextDto;
}

export interface InquiryFieldAssistResponseDto {
  text: string;
}

// --- One-shot expertise suggestion (catalog IDs + optional custom labels via structured extraction) ---

export interface InquiryExpertiseSuggestContextDto {
  title?: string;
  objectives?: string;
  specification?: string;
  optionalReqs?: string;
  /** Currently selected expertise IDs - optional refinement signal */
  currentExpertiseIds?: LookupItemDto['id'][];
}

export interface InquiryExpertiseSuggestRequestDto {
  /** Optional extra hints or focus from the user */
  input?: string;
  locale?: string;
  context?: InquiryExpertiseSuggestContextDto;
}

export interface InquiryExpertiseSuggestResponseDto {
  /** Catalog IDs and/or custom label strings to attach on inquiry save */
  expertiseIds: LookupItemDto['id'][];
}

// --- One-shot KPI suggestion ---

export interface InquiryKpiSuggestContextDto {
  title?: string;
  objectives?: string;
  categoryId?: string;
  industryIds?: string[];
}

export interface InquiryKpiSuggestRequestDto {
  /** Optional extra hints or focus from the user */
  input?: string;
  locale?: string;
  context?: InquiryKpiSuggestContextDto;
}

export interface InquiryKpiSuggestItemDto {
  description: string;
  value?: number;
  unit?: string;
}

export interface InquiryKpiSuggestResponseDto {
  kpis: InquiryKpiSuggestItemDto[];
}

// --- One-shot offer form assist ---

export const OfferAssistMaxLength = {
  Proposal: 4000,
  OutOfScope: 2000,
} as const;

export interface OfferAssistInquiryContextDto {
  inquiryId: string;
  title?: string;
  objectives?: string;
  specification?: string;
  startDate?: string;
  endDate?: string;
  budgetMax?: number;
  budgetCurrency?: string;
  categoryId?: string;
  industryIds?: string[];
}

export interface OfferScopeAssistRequestDto {
  input?: string;
  locale?: string;
  context: OfferAssistInquiryContextDto;
  currentProposal?: string;
  currentOutOfScope?: string;
}

export interface OfferScopeAssistResponseDto {
  proposal: string;
  outOfScope: string;
}

export type OfferMilestoneSuggestItemDto = Required<
  Pick<
    LlmChatOfferDraftMilestone,
    'name' | 'startDate' | 'output' | 'acceptanceCriteria' | 'dueDate' | 'price'
  >
> &
  Pick<LlmChatOfferDraftMilestone, 'requiredCooperation'>;

export interface OfferMilestonesSuggestRequestDto {
  input?: string;
  locale?: string;
  context: OfferAssistInquiryContextDto;
  currentProposal?: string;
  currentMilestones?: OfferMilestoneSuggestItemDto[];
  pricingModel?: PricingModel;
  currency?: Currency;
}

export interface OfferMilestonesSuggestResponseDto {
  milestones: OfferMilestoneSuggestItemDto[];
}

export type OfferRiskSuggestItemDto = Required<
  Pick<LlmChatOfferDraftRisk, 'risk' | 'mitigation'>
> &
  Pick<LlmChatOfferDraftRisk, 'probability' | 'impact'>;

export interface OfferRisksSuggestRequestDto {
  input?: string;
  locale?: string;
  context: OfferAssistInquiryContextDto;
  currentProposal?: string;
  milestonesSummary?: string;
  currency?: Currency;
}

export interface OfferRisksSuggestResponseDto {
  risks: OfferRiskSuggestItemDto[];
  sla?: string;
}

export interface OfferCaseStudiesSuggestRequestDto {
  input?: string;
  locale?: string;
  context: OfferAssistInquiryContextDto;
  currentCaseStudyIds?: string[];
}

export interface OfferCaseStudiesSuggestResponseDto {
  caseStudyIds: string[];
}

/** Record types a user can reference with `@` in the chat composer. */
export const EntityMentionType = {
  Inquiry: 'inquiry',
  Offer: 'offer',
  Project: 'project',
  DecisionCase: 'decisionCase',
  CaseStudy: 'caseStudy',
} as const;
export type EntityMentionType = (typeof EntityMentionType)[keyof typeof EntityMentionType];

export const ENTITY_MENTION_TYPES: EntityMentionType[] = [
  EntityMentionType.Inquiry,
  EntityMentionType.Offer,
  EntityMentionType.Project,
  EntityMentionType.DecisionCase,
  EntityMentionType.CaseStudy,
];

/**
 * One row of the record stage. The translated type prefix is a frontend display concern, so
 * only the bare `name` crosses the wire; the client renders `${t(prefix)}: ${name}`.
 */
export interface MentionableRecordDto {
  type: EntityMentionType;
  id: string;
  /** Bare record name, with no type prefix. */
  name: string;
  /** Distinguishing detail, so two records with the same name stay tellable apart. */
  detail: string;
  /** In-app destination. */
  url: string;
  updatedAt: string;
}

export interface MentionSearchResultDto {
  items: MentionableRecordDto[];
  /** True when more matches exist than were returned. */
  hasMore: boolean;
}

/** A reference resolved out of a message's content tokens. */
export interface LlmChatEntityReferenceDto {
  type: EntityMentionType;
  id: string;
  /** Current bare name when available; the name captured at send time when not. */
  name: string;
  /** False when the record was deleted or is no longer accessible. */
  available: boolean;
  /** Null when `available` is false. */
  url: string | null;
}

/** Built-in slash commands; the backend answers them without the LLM. */
export const LlmChatBuiltinSlashCommand = {
  Skills: 'skills',
} as const;
export type LlmChatBuiltinSlashCommand =
  (typeof LlmChatBuiltinSlashCommand)[keyof typeof LlmChatBuiltinSlashCommand];

export const LlmChatSlashCommandKind = {
  Builtin: 'builtin',
  Skill: 'skill',
} as const;
export type LlmChatSlashCommandKind =
  (typeof LlmChatSlashCommandKind)[keyof typeof LlmChatSlashCommandKind];

/** A command offered after `/` in the composer. `name` has no leading slash. */
export interface LlmChatSlashCommandDto {
  name: string;
  kind: LlmChatSlashCommandKind;
  /** Skill description from the skill file; null for built-ins, which the client translates. */
  description: string | null;
}
